import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useGetCurrentUser } from 'apis/user/hook';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useAgent } from 'providers/context/AgentProvider';
import { useEffect, useState } from 'react';
import { IoChevronDownSharp, IoWifiSharp } from 'react-icons/io5';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CacheKeys } from 'utils/cacheKeys';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import { MdOutlineLocationOn } from 'react-icons/md';
import PlusButtonIcon from '@/assets/icons/PlusButtonIcon';

export default function LocationDropdown() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { locationId } = useParams();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedBusiness, setSelectedBusiness, setSelectedLocation } = useBusiness();
  const { setSelectedAgentId } = useAgent();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<null | number>(null);
  const open = Boolean(anchorEl);

  const { data: currentUser, isLoading } = useGetCurrentUser();

  const { data: locations, isLoading: loadingLocation } = useGetLocations({}, {
    enabled: Boolean(selectedBusiness) && !isLoading
  } as any);

  const locationList = (() => {
    if (!locations?.data) return [];
    return locations.data;
  })();

  const persistLocationSelection = (location: { id: number; business_id: number }) => {
    localStorage.setItem(CacheKeys.businessIds, JSON.stringify([location.business_id]));
    localStorage.setItem(CacheKeys.locationIds, JSON.stringify([location.id]));
    setSelectedBusiness(location.business_id);
    setSelectedLocation(location.id);
  };

  useEffect(() => {
    if (locationList.length === 0) return;

    const preferredLocationId = locationId
      ? Number(locationId)
      : Number(JSON.parse(localStorage.getItem(CacheKeys.locationIds) ?? '[null]')?.[0]);

    if (preferredLocationId) {
      const index = locationList.findIndex((loc) => loc.id === preferredLocationId);
      if (index >= 0) {
        const preferred = locationList[index];
        setSelectedIndex(index);
        setSelectedLocation(preferred.id);
        if (selectedBusiness !== preferred.business_id) {
          localStorage.setItem(CacheKeys.businessIds, JSON.stringify([preferred.business_id]));
          localStorage.setItem(CacheKeys.locationIds, JSON.stringify([preferred.id]));
          setSelectedBusiness(preferred.business_id);
        }
        return;
      }
    }

    // Fallback → default to 0th index
    setSelectedIndex(0);
    persistLocationSelection(locationList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList, locationId, selectedBusiness, setSelectedLocation, setSelectedBusiness, currentUser?.role]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    const selected = locationList[index];
    if (selected) {
      persistLocationSelection(selected);
      localStorage.removeItem(CacheKeys.agentIds);
      setSelectedAgentId(undefined);
      navigate('/');
      setSelectedIndex(index);
    }
    setAnchorEl(null);
  };

  return (
    <>
      <List component='nav'>
        <ListItemButton
          id='location-button'
          aria-haspopup='listbox'
          aria-controls='location-menu'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}
          sx={{
            display: 'block',
            borderRadius: '8px',
            py: 0,
            px: 0,
            textWrap: 'nowrap',
            width: {
              xs: 200,
              md: 240
            }
          }}
        >
          {isMediumScreen ? (
            <Box
              display='flex'
              flexDirection='column'
              border='0.5px solid #ECECEC'
              borderRadius='4px'
              padding='8px'
              bgcolor={theme.palette.background.paper}
            >
              <Box display='flex' flexDirection='row' justifyContent='space-between'>
                <Typography>
                  {selectedIndex !== null && locationList[selectedIndex]
                    ? locationList[selectedIndex].name
                    : 'Select Location'}
                </Typography>
                <Box mt={0.5}>
                  <IoChevronDownSharp />
                </Box>
              </Box>
              <Typography color='secondary' fontSize={12} fontWeight={400}>
                {selectedIndex !== null && locationList[selectedIndex] ? locationList[selectedIndex].name : ''}
              </Typography>
            </Box>
          ) : (
            <Box display='flex' alignItems='center' gap={1} border='1px solid #ECECEC' px={1} borderRadius='8px'>
              <ListItemText>
                <Box borderRight='1px solid #ECECEC'>
                  <Typography fontSize={13} color='secondary'>
                    Current Location
                  </Typography>

                  {loadingLocation ? (
                    <Skeleton variant='text' width={120} height={14} />
                  ) : (
                    <Box display='flex' flexDirection='row' alignItems='center' gap={0.5} height={15} pb={0.5}>
                      {selectedIndex !== null &&
                        locationList[selectedIndex] &&
                        (locationList[selectedIndex]?.physical_location_exists ? (
                          <MdOutlineLocationOn size={18} color={theme.palette.primary.main} />
                        ) : (
                          <IoWifiSharp size={18} color={theme.palette.primary.main} />
                        ))}

                      <Typography noWrap fontSize={14} fontWeight={{ xs: 500, md: 600 }}>
                        {selectedIndex !== null && locationList[selectedIndex]
                          ? locationList[selectedIndex].name
                          : 'Select Location'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ListItemText>

              <IoChevronDownSharp color='#777777' />
            </Box>
          )}
        </ListItemButton>
      </List>

      <Menu
        id='location-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 250, maxHeight: 600, overflowY: 'auto' }
        }}
        MenuListProps={{
          'aria-labelledby': 'location-button',
          role: 'listbox'
        }}
      >
        {currentUser?.role !== 'location_admin' && (
          <>
            <Box
              display='flex'
              gap={1}
              alignItems='center'
              borderBottom='2px solid #F3F3F3'
              color={theme.palette.secondary.main}
              mx={1}
              py={0.5}
              mb={0.5}
              component={Link}
              to={'/setting/locations/add'}
              onClick={handleMenuClose}
            >
              <PlusButtonIcon />
              <Typography fontSize={16} color='primary'>
                Add new Location
              </Typography>
            </Box>
          </>
        )}

        {locationList.map((option, index) => (
          <MenuItem
            key={option.id}
            selected={index === selectedIndex}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            <Box
              color={index === selectedIndex ? '#000' : theme.palette.secondary.main}
              fontSize={14}
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={0.5}
              sx={{ width: '100%', overflow: 'hidden' }}
            >
              {option.physical_location_exists ? <MdOutlineLocationOn size={18} /> : <IoWifiSharp size={18} />}

              <Typography noWrap>{option.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
